import {
  endOfMonth,
  format,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
} from "date-fns";

export type FinanceDatePreset =
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "this_year"
  | "custom";

export interface FinanceDateRangeValue {
  from: string;
  to: string;
  preset: FinanceDatePreset;
}

export const FINANCE_REPORTING_TIMEZONE = "America/New_York";

const isoDate = (date: Date) => format(date, "yyyy-MM-dd");

// Returns a Date whose local fields match the wall-clock time in `timeZone`,
// so date-fns calendar math (start/end of month, etc.) resolves in that zone.
function zonedNowValue(now: Date, timeZone: string): Date {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(now);

  const lookup = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");

  const hour = lookup("hour");

  return new Date(
    lookup("year"),
    lookup("month") - 1,
    lookup("day"),
    hour === 24 ? 0 : hour,
    lookup("minute"),
    lookup("second"),
  );
}

export function financeToday(
  now = new Date(),
  timeZone = FINANCE_REPORTING_TIMEZONE,
): Date {
  return zonedNowValue(now, timeZone);
}

export function financeDateRange(
  preset: Exclude<FinanceDatePreset, "custom">,
  now = new Date(),
  timeZone = FINANCE_REPORTING_TIMEZONE,
): FinanceDateRangeValue {
  const zonedNow = financeToday(now, timeZone);

  if (preset === "last_month") {
    const previousMonth = subMonths(zonedNow, 1);
    return {
      from: isoDate(startOfMonth(previousMonth)),
      to: isoDate(endOfMonth(previousMonth)),
      preset,
    };
  }

  if (preset === "this_quarter") {
    return {
      from: isoDate(startOfQuarter(zonedNow)),
      to: isoDate(zonedNow),
      preset,
    };
  }

  if (preset === "this_year") {
    return {
      from: isoDate(startOfYear(zonedNow)),
      to: isoDate(zonedNow),
      preset,
    };
  }

  return {
    from: isoDate(startOfMonth(zonedNow)),
    to: isoDate(zonedNow),
    preset: "this_month",
  };
}

export function financeRangeFromSearch(
  search: string | URLSearchParams,
  timeZone = FINANCE_REPORTING_TIMEZONE,
  now = new Date(),
): FinanceDateRangeValue {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  const from = params.get("from");
  const to = params.get("to");

  if (from && to) {
    const today = isoDate(financeToday(now, timeZone));
    const safeTo = to > today ? today : to;
    const safeFrom = from > safeTo ? safeTo : from;

    return { preset: "custom", from: safeFrom, to: safeTo };
  }

  return financeDateRange("this_month", now, timeZone);
}

export function financeRangeSearch(
  range: Pick<FinanceDateRangeValue, "from" | "to">,
  extras: Record<string, string | number | null | undefined> = {},
): string {
  const params = new URLSearchParams();
  params.set("from", range.from);
  params.set("to", range.to);

  for (const [key, value] of Object.entries(extras)) {
    if (value !== null && value !== undefined && value !== "") {
      params.set(key, String(value));
    }
  }

  const query = params.toString();
  return query ? `?${query}` : "";
}
